import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { AuthCodeInput, AuthHeader, Button, ButtonCall, ButtonRetry, ButtonText } from 'components';
import { theme, toRem } from 'design';
import { routeConstants } from 'routes';
import Form from 'screens/Error/Form';
import { accountRecoveryInit } from 'store/AccountRecovery';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { mfaResendRequest, mfaVerifyRequest } from 'store/App';
import * as ActionTypes from 'store/App/types';
import { IStore } from 'store/types';
import { ChannelType, IApiErrorPayloadWithCode, IAuthMFARequired } from 'types';

const StyledButtonText = styled(ButtonText)`
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(13.75)};
  color: ${theme.color.primary[500]};

  &:disabled {
    color: ${theme.color.neutral[400]};
    cursor: auto;
  }

  &:hover:not(:disabled) {
    color: ${theme.color.primary[600]};
  }
`;

const StyledButton = styled(Button)`
  margin-top: ${toRem(33)};

  &:disabled {
    color: ${theme.color.neutral[400]};
  }
`;

const StyledButtonRetry = styled(ButtonRetry)`
  margin-top: ${toRem(9)};

  &:disabled {
    color: ${theme.color.neutral[400]};
  }
`;

interface IAuthMFAVerificationPropsBase {
  mfaRequired: IAuthMFARequired;
  onSuccess?: () => void;
}

interface IAuthMFAVerificationProps extends IAuthMFAVerificationPropsBase {
  onBack: () => void;
  authHeader?: never;
}

interface IConnectIntegrationMFAVerificationProps extends IAuthMFAVerificationPropsBase {
  authHeader: React.ReactNode;
  onBack?: never;
}

type MFAVerificationProps = IAuthMFAVerificationProps | IConnectIntegrationMFAVerificationProps;

const AuthMFAVerification: React.FC<MFAVerificationProps> = ({ mfaRequired, onBack, onSuccess, authHeader }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.App.MFA_VERIFY_REQUEST));
  const resendLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.App.MFA_RESEND_REQUEST));
  const apiErrorsVerify = useSelector((state: IStore) => selectAPIRequestErrors(state, ActionTypes.App.MFA_VERIFY_REQUEST)) as IApiErrorPayloadWithCode;
  const [hasResentPasscode, setHasResentPasscode] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    code: Yup.string().required('A 6 digit code must be entered').min(6, 'A 6 digit code must be entered').max(6, 'A 6 digit code must be entered'),
  });

  const { handleSubmit, dirty, isValid, setFieldValue } = useFormik<{ code: string }>({
    initialValues: { code: '' },
    onSubmit: (values: { code: string }) => {
      dispatch(mfaVerifyRequest(mfaRequired.authId, values.code, onSuccess));
    },
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema,
  });

  const resendSms = () => {
    setHasResentPasscode(true);
    dispatch(mfaResendRequest(mfaRequired.authId));
  };

  const resendCall = () => {
    dispatch(mfaResendRequest(mfaRequired.authId, ChannelType.CALL));
  };

  const initializeAccountRecovery = () => {
    dispatch(accountRecoveryInit(mfaRequired.authId, mfaRequired.hasDob, mfaRequired.hasPin));
    history.push(routeConstants.public.accountRecovery.root);
  };

  const renderAuthHeader = () => <AuthHeader data-cy="auth-mfa-verify-header" title="Authenticate Your Account" subtitle="Enter the code sent to your mobile phone" onBack={onBack} value={`xxx-xxx-${mfaRequired.phoneLast4}`} />;

  return (
    <>
      {authHeader || renderAuthHeader()}
      <Form onSubmit={handleSubmit}>
        <AuthCodeInput errorMessages={apiErrorsVerify?.errorMessages} onChange={(val) => setFieldValue('code', val)} />
        <StyledButton data-cy="submit-button" type="submit" label="Continue" disabled={isLoading || !(isValid && dirty) || apiErrorsVerify?.statusCode === '423'} isProcessing={isLoading} />
      </Form>
      <StyledButtonRetry
        dataCy={resendLoading ? 'resend-verification-code-loading' : 'resend-verification-code'}
        label="Resend verification code"
        onClick={resendSms}
        isLoading={resendLoading}
        disabled={apiErrorsVerify?.statusCode === '423'}
      />
      {hasResentPasscode && resendCall && <ButtonCall onClick={resendCall} />}
      <StyledButtonText onClick={() => initializeAccountRecovery()} label="No longer have access to this mobile number?" data-cy="account-recovery-link" disabled={apiErrorsVerify?.statusCode === '423'} />
    </>
  );
};

export default AuthMFAVerification;
